import { Temporal } from '@js-temporal/polyfill'
import { useEventListener, useMouseInElement } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import { useBoolean } from '@/src/hooks/useBoolean'
import { TIME_LINE_INTERVAL_MINUTES } from '@/src/lib/constants'
import { minutesToPlainTime } from '@/src/lib/dateTimeUtils'
import { pxToIntervalMinutes } from '@/src/lib/timelineUtils'

const mouseXToPlainTime = (mouseX: number): Temporal.PlainTime => minutesToPlainTime(pxToIntervalMinutes(mouseX))

type UseDraggingTimelineReturn = {
  startTime: Ref<Temporal.PlainTime>
  endTime: Ref<Temporal.PlainTime>
  dragging: Ref<boolean>
}

type OnEndArgs = {
  startTime: Temporal.PlainTime
  endTime: Temporal.PlainTime
}

type UseDraggingTimelineOptions = {
  onEnd?: (options: OnEndArgs) => void
  enabled?: Ref<boolean>
}

export const useDraggingTimeline = (
  target: Ref<HTMLElement | undefined>,
  options: UseDraggingTimelineOptions,
): UseDraggingTimelineReturn => {
  const enabled = computed(() => options.enabled?.value ?? true)
  const { elementX, isOutside } = useMouseInElement(target)
  const [dragging, setDragging] = useBoolean(false)
  const startX = ref(0)
  const endX = ref(0)
  const startTime = computed(() => mouseXToPlainTime(Math.min(startX.value, endX.value)))
  // NOTE: 最小で TIME_LINE_INTERVAL_MINUTES 分は選択状態となるように
  const minimumEndTime = computed<Temporal.PlainTime>(() =>
    startTime.value.add({ minutes: TIME_LINE_INTERVAL_MINUTES }),
  )

  const endTime = computed(() => {
    const rawEndTime = mouseXToPlainTime(Math.max(startX.value, endX.value))
    return Temporal.PlainTime.compare(rawEndTime, minimumEndTime.value) === 1 ? rawEndTime : minimumEndTime.value
  })

  watch([elementX, isOutside], () => {
    if (!enabled.value || isOutside.value || !dragging.value) return

    endX.value = elementX.value
  })

  const handleDown = () => {
    if (!enabled.value) return

    setDragging.on()
    startX.value = elementX.value
    endX.value = elementX.value
  }
  const handleUp = () => {
    if (!enabled.value || !dragging.value) return

    setDragging.off()

    if (options?.onEnd) {
      options.onEnd({
        startTime: startTime.value,
        endTime: endTime.value,
      })
    }
  }

  useEventListener(target, 'mousedown', handleDown)
  // NOTE: マウスダウンの開始が対象要素であればマウスアップはどこでも有効とするほうが動作が自然なので
  useEventListener('mouseup', handleUp)

  return { startTime, endTime, dragging }
}
